import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Maximize2, Volume2, VolumeX } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const VIDEO_SRC = '/media/hero-tour.mp4';
const POSTER_SRC = '/media/hero-tour-poster.webp';

const controlButton =
  'flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-md transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--mk-mint))]';

/**
 * Homepage hero video: plays muted on a loop (as browsers require for
 * autoplay), with a sound toggle and a "watch full size" view. People who ask
 * for reduced motion see the poster and press play themselves.
 */
export function HeroVideo({ className, style }: { className?: string; style?: CSSProperties }) {
  const inlineRef = useRef<HTMLVideoElement>(null);
  const largeRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [open, setOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    setReducedMotion(reduce);
    if (!reduce) inlineRef.current?.play().catch(() => {});
  }, []);

  const toggleSound = () => {
    const video = inlineRef.current;
    if (!video) return;
    const next = !muted;
    video.muted = next;
    setMuted(next);
    if (!next) video.play().catch(() => {});
  };

  const openLarge = () => {
    inlineRef.current?.pause();
    setOpen(true);
  };

  const onOpenChange = (next: boolean) => {
    if (!next) {
      const inline = inlineRef.current;
      const large = largeRef.current;
      if (inline && large) inline.currentTime = large.currentTime;
      if (inline && !reducedMotion) inline.play().catch(() => {});
    }
    setOpen(next);
  };

  return (
    <div className={cn('relative', className)} style={style} data-testid="hero-video">
      <div className="overflow-hidden rounded-[1.75rem] bg-black shadow-[0_40px_90px_-30px_rgba(10,6,40,0.9)] ring-1 ring-white/25">
        <video
          ref={inlineRef}
          className="block aspect-video h-auto w-full object-cover"
          src={VIDEO_SRC}
          poster={POSTER_SRC}
          muted={muted}
          loop
          playsInline
          autoPlay={!reducedMotion}
          preload="metadata"
          aria-label="Video: a quick tour of The Polished Version"
        />
      </div>

      <div className="absolute bottom-4 left-4 flex gap-2">
        <button
          type="button"
          onClick={toggleSound}
          className={controlButton}
          aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
          aria-pressed={!muted}
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
        <button type="button" onClick={openLarge} className={controlButton} aria-label="Watch full size">
          <Maximize2 className="h-5 w-5" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[min(92vw,153vh)] border-0 bg-black p-0 sm:rounded-2xl">
          <DialogTitle className="sr-only">A quick tour of The Polished Version</DialogTitle>
          <video
            ref={largeRef}
            className="block aspect-video h-auto w-full rounded-2xl"
            src={VIDEO_SRC}
            poster={POSTER_SRC}
            controls
            autoPlay
            playsInline
            onLoadedMetadata={(e) => {
              const inline = inlineRef.current;
              if (inline) e.currentTarget.currentTime = inline.currentTime;
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
